import { Observable } from 'rxjs';

import { Injectable } from '@angular/core';

import { FsMapIncidentManagementServiceProvider, IFsMap } from '@lib/geo/core';
import { SnMapView } from 'libs/api/entities/src/lib/map-view';

@Injectable()
export class MapIncidentManagementFacadeService<
  TMap extends IFsMap,
  TDirectionsRoute,
  TMarker,
  TOverlayType,
  TRoutePolyline,
  TShape = any,
  TCombined = TShape | TMarker,
> extends FsMapIncidentManagementServiceProvider<
  TMap,
  TDirectionsRoute,
  TMarker,
  TOverlayType,
  TRoutePolyline,
  TShape,
  TCombined
> {
  constructor(
    private readonly provider: FsMapIncidentManagementServiceProvider<
      TMap,
      TDirectionsRoute,
      TMarker,
      TOverlayType,
      TRoutePolyline,
      TShape,
      TCombined
    >,
  ) {
    super();
  }

  public get currentView(): SnMapView {
    return this.provider.currentView;
  }

  public set currentView(value: SnMapView) {
    this.provider.currentView = value;
  }

  public get map(): TMap {
    return this.provider.map;
  }

  public get shapes(): { shape: any; data: any }[] {
    return this.provider.shapes;
  }

  public get routeResults(): TDirectionsRoute[] {
    return this.provider.routeResults;
  }

  public get existingRoute(): TDirectionsRoute[] {
    return this.provider.existingRoute;
  }

  public set existingRoute(value: TDirectionsRoute[]) {
    this.provider.existingRoute = value;
  }

  public get totalRouteTime(): number {
    return this.provider.totalRouteTime;
  }

  public set totalRouteTime(value: number) {
    this.provider.totalRouteTime = value;
  }

  public get totalDistance(): number {
    return this.provider.totalDistance;
  }

  public set totalDistance(value: number) {
    this.provider.totalDistance = value;
  }

  public get addToDirections(): any[] {
    return this.provider.addToDirections;
  }

  public set addToDirections(value: any[]) {
    this.provider.addToDirections = value;
  }

  public get canChange(): boolean {
    return this.provider.canChange;
  }

  public get isAddingNotes(): boolean {
    return this.provider.isAddingNotes;
  }

  public set isAddingNotes(value: boolean) {
    this.provider.isAddingNotes = value;
  }

  public get isDrawing(): boolean {
    return this.provider.isDrawing;
  }

  public set isDrawing(value: boolean) {
    this.provider.isDrawing = value;
  }

  public get isAddingDirection(): boolean {
    return this.provider.isAddingDirection;
  }

  public set isAddingDirection(value: boolean) {
    this.provider.isAddingDirection = value;
  }

  public get clickoutHandler(): (event: unknown) => void {
    return this.provider.clickoutHandler;
  }

  public set clickoutHandler(value: (event: unknown) => void) {
    this.provider.clickoutHandler = value;
  }

  public get shapeComplete$(): Observable<TCombined> {
    return this.provider.shapeComplete$;
  }

  public get shouldDisableShapeCreation(): boolean {
    return this.provider.shouldDisableShapeCreation;
  }

  public get routePolyline(): TRoutePolyline {
    return this.provider.routePolyline;
  }

  public get activeFillColor(): string {
    return this.provider.activeFillColor;
  }

  public set activeFillColor(value: string) {
    this.provider.activeFillColor = value;
  }

  public get activeStrokeColor(): string {
    return this.provider.activeStrokeColor;
  }

  public set activeStrokeColor(value: string) {
    this.provider.activeStrokeColor = value;
  }

  public get isDeleting(): boolean {
    return this.provider.isDeleting;
  }

  public set isDeleting(value: boolean) {
    this.provider.isDeleting = value;
  }

  public get isLoadingVectors(): boolean {
    return this.provider.isLoadingVectors;
  }

  public set isLoadingVectors(value: boolean) {
    this.provider.isLoadingVectors = value;
  }

  public get fillOpacity(): number {
    return this.provider.fillOpacity;
  }

  public set fillOpacity(value: number) {
    this.provider.fillOpacity = value;
  }

  public get strokeOpacity(): number {
    return this.provider.strokeOpacity;
  }

  public set strokeOpacity(value: number) {
    this.provider.strokeOpacity = value;
  }

  public get strokeWeight(): number {
    return this.provider.strokeWeight;
  }

  public set strokeWeight(value: number) {
    this.provider.strokeWeight = value;
  }

  public onMapLoaded(map: TMap): void {
    this.provider.onMapLoaded(map);
  }

  public setCurrentView(view: SnMapView): void {
    this.provider.setCurrentView(view);
  }

  public removeTab(view: SnMapView): void {
    this.provider.removeTab(view);
  }

  public updateTab(view: SnMapView): void {
    this.provider.updateTab(view);
  }

  public selectDirectionMarkers(): void {
    this.provider.selectDirectionMarkers();
  }

  public setDrawingMode(drawingMode?: TOverlayType): void {
    this.provider.setDrawingMode(drawingMode);
  }

  public getDrawingMode(): TOverlayType {
    return this.provider.getDrawingMode();
  }

  public enterDrawingMode(): void {
    this.provider.enterDrawingMode();
  }

  public leaveDrawingMode(reset: boolean): void {
    this.provider.leaveDrawingMode(reset);
  }

  public clearShapes(): void {
    this.provider.clearShapes();
  }

  public editShapes(): void {
    this.provider.editShapes();
  }

  public setMarker(marker: TMarker): void {
    this.provider.setMarker(marker);
  }

  public addShape(type: string): void {
    this.provider.addShape(type);
  }

  public saveShape(shape: TCombined, data: any): void {
    this.provider.saveShape(shape, data);
  }

  public deleteShape(shape?: TCombined): void {
    this.provider.deleteShape(shape);
  }

  public isActiveShape(type: string): boolean {
    return this.provider.isActiveShape(type);
  }

  public generateRoute(): void {
    this.provider.generateRoute();
  }

  public checkIconRoutes(): void {
    this.provider.checkIconRoutes();
  }

  public saveRoute(addToDirections: any[]): Observable<SnMapView> {
    return this.provider.saveRoute(addToDirections);
  }
}
